import { tx, loc } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatError } from "@/lib/api";
import { AppShell, Loader } from "@/components/AppShell";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, Wallet, Clock, ShieldCheck, Sparkles, Ticket, Check } from "lucide-react";

const eur = (cents) =>
  (cents / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " \u20ac";


const addInterval = (interval) => {
  const d = new Date();
  if (interval === "year") d.setFullYear(d.getFullYear() + 1);
  else d.setMonth(d.getMonth() + 1);
  return d.toLocaleDateString(loc(), { day: "numeric", month: "long", year: "numeric" });
};

const intervalLabel = (i) => tx(i === "year" ? "all'anno" : "al mese");

export default function CheckoutSummary() {
  const { lookup_key } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [invite, setInvite] = useState(null); // { code, discount_percent }

  useEffect(() => {
    api.get("/plans").then((r) => {
      const p = r.data.plans.find((x) => x.lookup_key === lookup_key);
      if (!p) { toast.error(tx("Piano non trovato")); navigate("/pricing"); return; }
      setPlan(p);
    }).catch((err) => toast.error(formatError(err.response?.data?.detail)));
  }, [lookup_key, navigate]);

  const verifyCode = async () => {
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const { data } = await api.post("/invite-codes/validate", { code: code.trim() });
      setInvite({ code: data.code, discount_percent: data.discount_percent });
      toast.success(tx("Invito personale applicato"));
    } catch (err) {
      setInvite(null);
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setVerifying(false);
    }
  };

  const removeCode = () => { setInvite(null); setCode(""); };

  const pay = async () => {
    setBusy(true);
    try {
      const body = { lookup_key, origin_url: window.location.origin };
      if (invite) body.code = invite.code;
      const { data } = await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!plan) return <Loader />;

  const renewalDate = addInterval(plan.renewal_interval);
  const discountedToday = invite ? Math.round(plan.renewal_amount * (100 - invite.discount_percent) / 100) : null;
  const renewalDisplay = plan.renewal_interval === "year" ? (plan.ordinary_display || "79") : (plan.price_display || "9,90");
  const noteTpl = plan.renewal_interval === "year"
    ? "Il tuo invito personale: {amount} per il primo anno. Successivamente {renewal} \u20ac/anno, con rinnovo automatico salvo disdetta."
    : "Il tuo invito personale: {amount} per il primo mese. Successivamente {renewal} \u20ac/mese, con rinnovo automatico salvo disdetta.";
  const inviteNote = invite
    ? tx(noteTpl).replace("{amount}", eur(discountedToday)).replace("{renewal}", renewalDisplay)
    : null;

  const Line = ({ icon: Icon, label, value, sub }) => (
    <div className="flex items-start gap-3 py-3.5 border-b border-anima-ink/8 last:border-0">
      <Icon size={18} className="text-anima-sagedeep mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-xs uppercase tracking-widest text-anima-muted">{label}</p>
        <p className="text-anima-ink mt-0.5">{value}</p>
        {sub && <p className="text-xs text-anima-muted mt-0.5">{sub}</p>}
      </div>
    </div>
  );

  return (
    <AppShell nav={false}>
      <div className="anima-backdrop min-h-screen">
        <div className="px-7 pt-10 pb-14 max-w-md mx-auto">
          <button onClick={() => navigate("/pricing")} className="text-anima-muted mb-8 flex items-center gap-1 text-sm" data-testid="summary-back">
            <ArrowLeft size={16} />{tx("Torna ai piani")}</button>

          <h1 className="font-serif text-4xl text-anima-ink mb-2" data-testid="summary-title">{tx("Riepilogo")}</h1>
          <p className="text-anima-muted mb-6">{tx("Piano")} {tx(plan.name)}{!(invite && plan.promo) && <> &middot; {tx(plan.label)}</>}</p>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="rounded-3xl bg-white/65 border border-anima-ink/8 p-6 mb-6"
          >
            <Line
              icon={Wallet}
              label={tx("Importo da pagare oggi")}
              value={
                invite ? (
                  <span className="flex items-baseline gap-2">
                    <span data-testid="summary-today-amount" className="font-serif text-2xl text-anima-golddeep">{eur(discountedToday)}</span>
                    <span className="text-sm text-anima-muted line-through">{eur(plan.renewal_amount)}</span>
                  </span>
                ) : (
                  <span data-testid="summary-today-amount" className="font-serif text-2xl">{eur(plan.today_amount)}</span>
                )
              }
            />
            <Line
              icon={Clock}
              label={tx("Durata acquistata")}
              value={<span data-testid="summary-duration">{tx(plan.duration_label)}</span>}
            />
            <Line
              icon={CalendarClock}
              label={tx("Prossimo rinnovo automatico")}
              value={<span data-testid="summary-renewal">{renewalDate}</span>}
              sub={`${eur(plan.renewal_amount)} ${intervalLabel(plan.renewal_interval)}`}
            />
            <Line
              icon={ShieldCheck}
              label={tx("Disattivazione")}
              value={tx("Puoi disattivare il rinnovo automatico quando vuoi")}
              sub={tx("Dal tuo Profilo. L'accesso resta attivo fino alla fine del periodo già pagato.")}
            />
          </motion.div>

          {/* Codice personale */}
          <div className="rounded-2xl bg-white/55 border border-anima-ink/8 p-4 mb-6" data-testid="invite-code-box">
            {!invite ? (
              <>
                {!showCode ? (
                  <button onClick={() => setShowCode(true)} data-testid="invite-code-toggle" className="flex items-center gap-2 text-sm text-anima-sagedeep">
                    <Ticket size={16} />{tx("Hai un codice personale?")}</button>
                ) : (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-anima-muted flex items-center gap-1.5 mb-2"><Ticket size={14} />{tx("Hai un codice personale?")}</label>
                    <div className="flex gap-2">
                      <input
                        data-testid="invite-code-input" value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") verifyCode(); }}
                        placeholder={tx("ANIMA-XXXX-XXXX")}
                        className="flex-1 rounded-xl bg-white/70 border border-anima-ink/12 px-3 py-2.5 outline-none text-sm tracking-wide"
                      />
                      <button onClick={verifyCode} disabled={verifying} data-testid="invite-code-verify" className="rounded-xl bg-anima-sagedeep text-anima-ivory px-4 text-sm disabled:opacity-60">
                        {verifying ? "..." : tx("Verifica")}
                      </button>
                    </div>
                    <p className="text-[11px] text-anima-muted mt-2">{tx("Il codice è riservato al tuo indirizzo email e vale per un solo acquisto.")}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-start justify-between gap-3" data-testid="invite-applied">
                <div className="flex items-start gap-2">
                  <Check size={16} className="text-anima-sagedeep mt-0.5" />
                  <div>
                    <p className="text-sm text-anima-ink font-medium">{tx("Invito personale applicato")}<span className="font-mono">{invite.code}</span></p>
                    <p className="text-xs text-anima-sagedeep mt-1" data-testid="invite-note">{inviteNote}</p>
                  </div>
                </div>
                <button onClick={removeCode} data-testid="invite-remove" className="text-xs text-anima-muted underline shrink-0">{tx("Rimuovi")}</button>
              </div>
            )}
          </div>

          {plan.promo && !invite && (
            <div className="rounded-2xl bg-anima-gold/15 border border-anima-gold/40 p-4 mb-6" data-testid="summary-promo">
              <p className="text-sm text-anima-ink">{tx(plan.note)}</p>
            </div>
          )}

          <button
            data-testid="summary-pay-btn" onClick={pay} disabled={busy}
            className="w-full rounded-full bg-anima-sagedeep text-anima-ivory py-4 font-medium active:scale-95 transition-transform duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <Sparkles size={17} />
            {busy ? tx("Ti porto al pagamento...") : tx("Procedi al pagamento")}
          </button>

          <p className="text-[11px] text-anima-muted text-center mt-5 leading-relaxed">{tx("Pagamenti sicuri gestiti da Stripe. I contenuti si sbloccano solo dopo la conferma del pagamento.")}<br />{tx("Procedendo accetti i")}<Link to="/terms" className="underline text-anima-sagedeep">{tx("Termini")}</Link> e la{" "}
            <Link to="/privacy" className="underline text-anima-sagedeep">{tx("Privacy Policy")}</Link>.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
